/**
 * Johnson Criteria — thermal camera range calculation per NATO STANAG 4347
 * and Johnson 1958.
 *
 * Formula (single-pixel angular resolution model):
 *
 *   R_max = (target_critical_dimension_m × focal_length_mm)
 *         / (cycles_required × pixel_pitch_mm)
 *
 * where `cycles_required` is the Johnson N value for the task (1 / 4 / 8 for
 * Detect / Recognize / Identify per the classic 1958 formulation).
 *
 * Two target conventions are supported because manufacturers split between them:
 *   - NATO STANAG 4347 — person 0.75×1.8m, vehicle 2.3×2.3m. Used by Axis, Bosch, FLIR.
 *   - Classic Johnson — person 0.5×1.8m, vehicle 1.4×4m. Used by Dahua, Hikvision.
 *
 * Validation: this implementation is tested against 78 verified DRI tables
 * published in manufacturer datasheets (see __tests__/johnsonCriteria.test.ts).
 * Tolerance ±10%; any spec failing falls back to "rangeFromDatasheet" lookup.
 *
 * References:
 *   - Johnson, J. "Analysis of Image Forming Systems" (1958)
 *   - NATO STANAG 4347 "Definition of nominal static range performance for thermal imaging systems"
 *   - EN 62676-4:2015 (visible cameras DORI — for parity with this module's API)
 */

import type {
  ThermalSpec,
  ThermalTask,
  ThermalTarget,
  JohnsonStandard,
  TargetDimensions,
} from './types';

/**
 * Johnson cycle counts per task (1958 classic — used unchanged by all major
 * security camera manufacturers in their published DRI tables).
 */
const JOHNSON_CYCLES: Record<ThermalTask, number> = {
  detect: 1,
  recognize: 4,
  identify: 8,
};

/** Critical dimensions per target per standard (in meters). */
const TARGET_DIMENSIONS: Record<JohnsonStandard, Record<ThermalTarget, TargetDimensions>> = {
  natoStanag4347: {
    person: { criticalDimension_m: 0.75, width_m: 0.75, height_m: 1.8 },
    vehicle: { criticalDimension_m: 2.3, width_m: 2.3, height_m: 2.3 },
  },
  classicJohnson: {
    person: { criticalDimension_m: 0.5, width_m: 0.5, height_m: 1.8 },
    vehicle: { criticalDimension_m: 1.4, width_m: 1.4, height_m: 4.0 },
  },
};

/**
 * Pick the Johnson target convention a manufacturer publishes its DRI tables
 * against. Dahua / Hikvision / Uniview use the classic 1958 person 0.5×1.8m
 * convention; everyone else (Axis, Bosch, FLIR, …) uses NATO STANAG 4347.
 * Single source of truth — callers (2D renderer, panel, PDF schedules,
 * validator, 3D scene) must use this instead of hardcoding a standard, or
 * the same camera reports a ~1.5× different thermal range across views.
 */
export function pickJohnsonStandard(manufacturer?: string): JohnsonStandard {
  switch (manufacturer) {
    case 'Dahua':
    case 'Hikvision':
    case 'Uniview':
      return 'classicJohnson';
    default:
      return 'natoStanag4347';
  }
}

export function getTargetDimensions(
  target: ThermalTarget,
  standard: JohnsonStandard = 'natoStanag4347',
): TargetDimensions {
  return TARGET_DIMENSIONS[standard][target];
}

/**
 * Calculate the maximum range (meters) at which a thermal camera can perform
 * a given task on a given target.
 *
 * Resolution priority (most authoritative first):
 *   1. Manufacturer-published value for this exact target+task — used verbatim.
 *   2. Manufacturer-published value for SIBLING task on same target —
 *      proportionally interpolated using Johnson cycle ratios. This avoids
 *      mixing datasheet absolute distances with formula absolute distances
 *      (different manufacturers use different target conventions, so the two
 *      scales rarely agree). Within a single datasheet the cycle ratio
 *      detect:recognize:identify = 1:1/4:1/8 always holds.
 *   3. Pure Johnson formula (target × focal / pitch / cycles).
 *
 * @returns range in meters, or null when calculation is impossible.
 */
export function calculateThermalRange(
  spec: ThermalSpec,
  task: ThermalTask,
  target: ThermalTarget,
  standard: JohnsonStandard = 'natoStanag4347',
): number | null {
  const targetDri = spec.datasheetDRI_m?.[target];

  // Priority 1: exact published value
  const exact = targetDri?.[task];
  if (typeof exact === 'number') return exact;

  // Priority 2: interpolate from a sibling published value on the same target,
  // preserving Johnson cycle ratios (1:4:8) so detect ≥ recognize ≥ identify
  // is always preserved, even when the manufacturer uses non-standard target
  // sizes (e.g. Uniview).
  if (targetDri) {
    const requestedCycles = JOHNSON_CYCLES[task];
    for (const siblingTask of ['detect', 'recognize', 'identify'] as ThermalTask[]) {
      const siblingValue = targetDri[siblingTask];
      if (typeof siblingValue === 'number' && siblingTask !== task) {
        const siblingCycles = JOHNSON_CYCLES[siblingTask];
        // R_task / R_sibling = cycles_sibling / cycles_task
        return siblingValue * (siblingCycles / requestedCycles);
      }
    }
  }

  // Priority 3: pure formula
  if (!spec.focalLength_mm || !spec.pixelPitch_um) return null;
  const dim = TARGET_DIMENSIONS[standard][target];
  const cyclesRequired = JOHNSON_CYCLES[task];
  const pixelPitch_mm = spec.pixelPitch_um / 1000;
  return (dim.criticalDimension_m * spec.focalLength_mm) / (cyclesRequired * pixelPitch_mm);
}

/**
 * How far a bi-spectral camera's thermal cone is drawn: its task distance, but
 * never past the range set on the camera. The range is what the camera is meant
 * to cover, and a person-detection distance of several hundred metres otherwise
 * ran the thermal cone far beyond a range the designer had cut short.
 */
export function drawnThermalRangeM(taskRangeM: number | null, cameraRangeM: number | undefined): number | null {
  if (taskRangeM === null || !(taskRangeM > 0)) return null;
  return cameraRangeM !== undefined && cameraRangeM > 0 ? Math.min(taskRangeM, cameraRangeM) : taskRangeM;
}

/** Convenience wrapper returning all 3 task ranges for a target. */
export function calculateAllDRI(
  spec: ThermalSpec,
  target: ThermalTarget,
  standard: JohnsonStandard = 'natoStanag4347',
): { detect: number | null; recognize: number | null; identify: number | null } {
  return {
    detect: calculateThermalRange(spec, 'detect', target, standard),
    recognize: calculateThermalRange(spec, 'recognize', target, standard),
    identify: calculateThermalRange(spec, 'identify', target, standard),
  };
}

/**
 * Compute the angular Instantaneous Field Of View per pixel, in milliradians.
 * Used for advanced reporting (printed in PDF Thermal Coverage Report).
 */
export function computeIFOV_mrad(spec: ThermalSpec): number | null {
  if (!spec.pixelPitch_um || !spec.focalLength_mm) return null;
  return (spec.pixelPitch_um / 1000 / spec.focalLength_mm) * 1000;
}
